/**
 * How small and how large an object on the canvas may be made, by its kind
 * (block flavour or element type). One table for everyone who sizes an object:
 * the canvas while the user drags a handle and the server when a tool writes
 * a size. A kind without an entry has the default limit.
 */
export interface CanvasResizeLimit {
  readonly minWidth: number;
  readonly minHeight: number;
  readonly maxWidth: number;
  readonly maxHeight: number;
}

export const CANVAS_DEFAULT_RESIZE_LIMIT: CanvasResizeLimit = Object.freeze({
  minWidth: 2,
  minHeight: 2,
  maxWidth: 5_000_000,
  maxHeight: 5_000_000,
});

/** Only what differs from the default. A height the object sets itself is not listed. */
export const CANVAS_RESIZE_LIMITS: Readonly<
  Record<string, Partial<CanvasResizeLimit>>
> = Object.freeze({
  'affine:note': { minWidth: 218, minHeight: 92 },
  'affine:attachment': { minWidth: 160, minHeight: 72 },
  'affine:embed-iframe': {
    minWidth: 218,
    minHeight: 44,
    maxWidth: 3400,
    maxHeight: 2200,
  },
  'affine:embed-html': { minWidth: 370, minHeight: 80 },
  'affine:embed-synced-doc': { minWidth: 370, minHeight: 48 },
  'affine:database': { minWidth: 280 },
  'affine:sticky-note': { minWidth: 160, minHeight: 130 },
  'affine:tutorial-card': { minWidth: 200, maxWidth: 400 },
  'affine:ghost': { minWidth: 180, maxWidth: 400 },
});

export function canvasResizeLimitOf(kind: string): CanvasResizeLimit {
  return { ...CANVAS_DEFAULT_RESIZE_LIMIT, ...CANVAS_RESIZE_LIMITS[kind] };
}

/** The side of a size that leaves the limit of its kind, or null when the size fits. */
export function canvasResizeViolation(
  kind: string,
  width: number,
  height: number
): { side: 'width' | 'height'; min: number; max: number } | null {
  const limit = canvasResizeLimitOf(kind);
  if (width < limit.minWidth || width > limit.maxWidth)
    return { side: 'width', min: limit.minWidth, max: limit.maxWidth };
  if (height < limit.minHeight || height > limit.maxHeight)
    return { side: 'height', min: limit.minHeight, max: limit.maxHeight };
  return null;
}
